import { useMemo } from "react";
import { getVal, blockRegistry, extractAllBlocksFromTpl, collectMarkIdsInOrder } from "@okf/ootils";

/**
 * Reading-order rank for each annotation, keyed by annoDataId.
 *
 * The doc-level rollup the V3 sidebar walks (annotations.tags.tags.data)
 * aggregates fragments across all fields and carries no field/position info, so
 * on its own the cards come out in tag-walk order, not document order. This
 * recovers true top-to-bottom reading order from the source of truth — the
 * Lexical mark nodes in each field's editorState:
 *   1. Fields in template-declaration order (getAnnotationEnabledBlocks).
 *   2. Within a field, collectMarkIdsInOrder walks `<base>.editorState` and
 *      returns mark-node ids in document order, deduped on FIRST occurrence
 *      (= where that anno's highlight first starts). A mark's `ids` carries every
 *      anno covering that text segment (overlaps merge into one segment) and a
 *      single anno is split across multiple segments, hence the first-occurrence
 *      dedup. It reads ONLY `.editorState`, never the sibling `.annoData`, whose
 *      per-anno `fragment` mini-states would inject out-of-order marks.
 *
 * Returns a Map<annoDataId, rank>. Annotations not found in the walk (orphaned
 * marks, non-annoDataId fallback ids) are simply absent — callers sink them to
 * the end.
 */
export const useAnnoOrderIndex = (contribution: any, tpl: any): Map<string, number> =>
  useMemo(() => {
    const index = new Map<string, number>();
    if (!contribution || !tpl) return index;

    let annoBlocks: any[] = [];
    try {
      annoBlocks = blockRegistry.getAnnotationEnabledBlocks(extractAllBlocksFromTpl({ tpl }) as any);
    } catch {
      annoBlocks = [];
    }

    let rank = 0;
    for (const block of annoBlocks) {
      // Blocks with a nested lexical value (e.g. AudioInput) hold editorState at
      // `<valuePath>.<lexicalSubPath>.editorState`; plain LexicalTextEditor
      // (lexicalValue: null) at `<valuePath>.editorState`. Mirror the path
      // resolution used by the annotation services (_getAnnotationStatusOverview).
      const lexicalSubPath = blockRegistry.getBlock(block.comp)?.fieldPaths?.lexicalValue;
      const base = lexicalSubPath ? `${block.valuePath}.${lexicalSubPath}` : block.valuePath;
      const editorState = getVal(contribution, `${base}.editorState`);
      if (!editorState) continue;
      // collectMarkIdsInOrder dedups within a field; the index-level guard keeps
      // first-field-wins across fields (an annoDataId belongs to one field anyway).
      for (const id of collectMarkIdsInOrder(editorState)) {
        if (!index.has(id)) index.set(id, rank++);
      }
    }
    return index;
  }, [contribution, tpl]);
